import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonGroup, Chip, CircularProgress, IconButton, Menu, MenuItem, Stack, TextField, Tooltip,
} from '@mui/material';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import UndoVariantIcon from 'mdi-react/UndoVariantIcon';
import RedoVariantIcon from 'mdi-react/RedoVariantIcon';
import KeyboardOutlineIcon from 'mdi-react/KeyboardOutlineIcon';
import ContentSaveOutlineIcon from 'mdi-react/ContentSaveOutlineIcon';
import DownloadOutlineIcon from 'mdi-react/DownloadOutlineIcon';
import PublishIcon from 'mdi-react/PublishIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import { editorTheme } from '../utils/editor-theme';

interface Props {
  readonly officeName: string;
  readonly storey: number | undefined;
  readonly storeys: number[];
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly onBack: () => void;
  readonly onStoreyChange: (storey: number) => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  /** Puts the changes live now. */
  readonly onSave: () => void;
  readonly onExport: () => void;
  readonly onShowShortcuts: () => void;
  /** Whether the plan has an unpublished draft, which is what Publish would put live. */
  readonly hasDraft?: boolean;
  /** Whether the plan has never been published: the app does not show it, and Publish puts it there as it stands. */
  readonly unpublished?: boolean;
  readonly onSaveDraft?: () => void;
  readonly onSchedule?: () => void;
  readonly onRemove?: () => void;
  /** Starts a plan for a storey this office has none for. */
  readonly onAddFloor?: () => void;
  /** Gives the open plan another floor number. */
  readonly onChangeStorey?: () => void;
}

/** The storey menu's entry for a new floor, which is not a storey number. */
const ADD_FLOOR = '__add-floor';

/** The storey menu's entry for renumbering the open floor, which is not a storey number either. */
const CHANGE_STOREY = '__change-storey';

const EditorTopBar = ({
  officeName,
  storey,
  storeys,
  dirty,
  saving,
  canUndo,
  canRedo,
  onBack,
  onStoreyChange,
  onUndo,
  onRedo,
  onSave,
  onExport,
  onShowShortcuts,
  hasDraft = false,
  unpublished = false,
  onSaveDraft,
  onSchedule,
  onRemove,
  onAddFloor,
  onChangeStorey,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [publishMenu, setPublishMenu] = useState<HTMLElement | null>(null);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        px: 1.5,
        py: 0.75,
        minHeight: 52,
        color: editorTheme.ink,
        backgroundColor: editorTheme.surface,
        borderBottom: `1px solid ${editorTheme.line}`,
      }}
    >
      <IconButton onClick={onBack} aria-label={String(t('back'))} size="small">
        <ArrowLeftIcon size={20} />
      </IconButton>

      <Box
        sx={{
          fontWeight: 650,
          fontSize: 14,
          letterSpacing: '-0.01em',
          color: editorTheme.ink,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 280,
        }}
        title={officeName}
      >
        {officeName}
      </Box>

      <TextField
        select
        size="small"
        label={String(t('floorplan-editor-storey'))}
        value={storey ?? ''}
        onChange={(event): void => {
          if (event.target.value === ADD_FLOOR) {
            onAddFloor?.();
            return;
          }

          if (event.target.value === CHANGE_STOREY) {
            onChangeStorey?.();
            return;
          }

          onStoreyChange(Number(event.target.value));
        }}
        sx={{ minWidth: 104, '& .MuiInputBase-root': { backgroundColor: editorTheme.surfaceSunken } }}
      >
        {storeys.map((value) => (
          <MenuItem key={value} value={value}>{value}</MenuItem>
        ))}
        {onChangeStorey && storey !== undefined && (
          <MenuItem value={CHANGE_STOREY}>{t('floorplan-editor-change-storey', { storey })}</MenuItem>
        )}
        {onAddFloor && <MenuItem value={ADD_FLOOR}>{t('floorplan-editor-add-floor')}</MenuItem>}
      </TextField>

      {unpublished && (
        <Tooltip title={String(t('floorplan-editor-unpublished-chip-hint'))}>
          <Chip size="small" color="warning" variant="outlined" label={String(t('floorplan-editor-unpublished-chip'))} />
        </Tooltip>
      )}

      {hasDraft && (
        <Tooltip title={String(t('floorplan-editor-draft-chip-hint'))}>
          <Chip size="small" color="info" variant="outlined" label={String(t('floorplan-editor-draft-chip'))} />
        </Tooltip>
      )}

      {/* A dot, not a chip. The unsaved state is permanent while somebody works — a chip of
          warning-coloured text sitting there for twenty minutes stops being read, and it
          takes the room a floor name needs. */}
      {dirty && (
        <Tooltip title={String(t('floorplan-editor-unsaved-changes'))}>
          <Box
            aria-label={String(t('floorplan-editor-unsaved-changes'))}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: editorTheme.warn,
              boxShadow: `0 0 0 3px ${editorTheme.warn}22`,
            }}
          />
        </Tooltip>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Tooltip title={`${t('floorplan-editor-undo')} (Ctrl+Z)`}>
        <span>
          <IconButton onClick={onUndo} disabled={!canUndo} aria-label={String(t('floorplan-editor-undo'))} size="small">
            <UndoVariantIcon size={20} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={`${t('floorplan-editor-redo')} (Ctrl+Shift+Z)`}>
        <span>
          <IconButton onClick={onRedo} disabled={!canRedo} aria-label={String(t('floorplan-editor-redo'))} size="small">
            <RedoVariantIcon size={20} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={`${t('floorplan-editor-shortcuts')} (?)`}>
        <IconButton onClick={onShowShortcuts} aria-label={String(t('floorplan-editor-shortcuts'))} size="small">
          {/* A keyboard, not a question mark: the sheet is a list of shortcuts, not help. */}
          <KeyboardOutlineIcon size={20} />
        </IconButton>
      </Tooltip>

      <Button
        size="small"
        variant="outlined"
        onClick={onExport}
        startIcon={<DownloadOutlineIcon size={16} />}
        // Sentence case, not MUI's shouting default: every other label in this tool is a
        // sentence, and a row of capitals reads as a warning rather than as a button.
        sx={{ textTransform: 'none', borderColor: editorTheme.line, color: editorTheme.inkDim }}
      >
        {t('floorplan-editor-export')}
      </Button>

      {onRemove && (
        <Tooltip title={String(t('floorplan-editor-remove'))}>
          <IconButton onClick={onRemove} aria-label={String(t('floorplan-editor-remove'))} size="small" disabled={saving}>
            <DeleteOutlineIcon size={20} />
          </IconButton>
        </Tooltip>
      )}

      {onSaveDraft && (
        <Button
          size="small"
          variant="outlined"
          onClick={onSaveDraft}
          disabled={!dirty || saving}
          startIcon={<ContentSaveOutlineIcon size={16} />}
          sx={{ textTransform: 'none' }}
        >
          {t('floorplan-editor-save-draft')}
        </Button>
      )}

      <ButtonGroup size="small" variant="contained" disableElevation>
        <Button
          onClick={onSave}
          disabled={(!dirty && !hasDraft && !unpublished) || saving}
          startIcon={saving ? <CircularProgress size={14} /> : <PublishIcon size={16} />}
          sx={{ textTransform: 'none' }}
        >
          {t('floorplan-editor-publish')}
        </Button>
        {onSchedule && (
          <Button
            aria-label={String(t('floorplan-editor-publish-options'))}
            onClick={(event): void => setPublishMenu(event.currentTarget)}
            disabled={(!dirty && !hasDraft && !unpublished) || saving}
            sx={{ px: 0.5, minWidth: 0 }}
          >
            <ChevronDownIcon size={16} />
          </Button>
        )}
      </ButtonGroup>

      <Menu anchorEl={publishMenu} open={publishMenu !== null} onClose={(): void => setPublishMenu(null)}>
        <MenuItem
          onClick={(): void => {
            setPublishMenu(null);
            onSave();
          }}
        >
          {t('floorplan-editor-publish-now')}
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setPublishMenu(null);
            onSchedule?.();
          }}
        >
          {t('floorplan-editor-publish-schedule')}
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default EditorTopBar;
